/**
 * What this platform gates and what this build supports.
 *
 * Onboarding's permission steps were written against macOS TCC. Windows gates
 * almost none of the same things — global hotkeys and screen capture need no
 * grant at all, and the microphone is a global settings toggle rather than a
 * per-app prompt — so asking the macOS questions there produces a wizard with
 * an "Allow" button that cannot do anything.
 *
 * Rust is the source of truth (see `platform_capabilities.rs`), because it also
 * knows which features are actually implemented on this platform: onboarding
 * should not demonstrate a gesture that is not wired up yet and then leave the
 * user waiting out a timeout. As each feature lands, its flag flips in Rust and
 * the flow picks it up with no change here.
 *
 * Reads are synchronous — the onboarding overlay renders before an invoke could
 * resolve — so the value is seeded from the platform and then replaced by the
 * authoritative answer.
 *
 * The seed duplicates the Rust tables, and nothing enforces that the two agree.
 * That is tolerable only because of where the seed is allowed to be believed:
 * synchronous reads decide what to RENDER for a frame, and a stale frame
 * corrects itself the moment resolution lands. The one decision that outlives a
 * frame — skipping an onboarding step, which gets persisted to the backend —
 * is gated on `platformCapabilitiesResolved()` and simply does not skip until
 * the real answer is in. Keep it that way: any new caller that persists or
 * otherwise commits to a capability must check resolution first.
 */
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import { isWindowsPlatform } from "@/lib/os-platform";

export type MicrophonePermissionStyle = "prompt" | "system_settings" | "none";

export type PlatformCapabilities = {
  requires_accessibility_permission: boolean;
  requires_screen_recording_permission: boolean;
  microphone_permission_style: MicrophonePermissionStyle;
  supports_hotkeys: boolean;
  supports_dictation: boolean;
  supports_screen_capture: boolean;
  /**
   * Whether the app must obtain its own consent before agents may synthesise
   * input and read the focused element. True only on Windows, which gates none
   * of it at the OS level - macOS answers this through the Accessibility grant.
   */
  requires_input_consent: boolean;
};

const MACOS_CAPABILITIES: PlatformCapabilities = {
  requires_accessibility_permission: true,
  requires_screen_recording_permission: true,
  microphone_permission_style: "prompt",
  supports_hotkeys: true,
  supports_dictation: true,
  supports_screen_capture: true,
  requires_input_consent: false,
};

const WINDOWS_CAPABILITIES: PlatformCapabilities = {
  requires_accessibility_permission: false,
  requires_screen_recording_permission: false,
  microphone_permission_style: "system_settings",
  supports_hotkeys: true,
  supports_dictation: true,
  supports_screen_capture: true,
  requires_input_consent: true,
};

let cached: PlatformCapabilities = isWindowsPlatform()
  ? WINDOWS_CAPABILITIES
  : MACOS_CAPABILITIES;
let resolved = false;
let inflight: Promise<PlatformCapabilities> | null = null;

function isStyle(value: unknown): value is MicrophonePermissionStyle {
  return value === "prompt" || value === "system_settings" || value === "none";
}

/** Coerce the Rust payload, falling back per-field to the seed rather than
 *  discarding the whole answer if one field is unrecognised. */
function parse(raw: unknown): PlatformCapabilities {
  const value = (raw ?? {}) as Partial<PlatformCapabilities>;
  const bool = (v: unknown, fallback: boolean) =>
    typeof v === "boolean" ? v : fallback;
  return {
    requires_accessibility_permission: bool(
      value.requires_accessibility_permission,
      cached.requires_accessibility_permission,
    ),
    requires_screen_recording_permission: bool(
      value.requires_screen_recording_permission,
      cached.requires_screen_recording_permission,
    ),
    microphone_permission_style: isStyle(value.microphone_permission_style)
      ? value.microphone_permission_style
      : cached.microphone_permission_style,
    supports_hotkeys: bool(value.supports_hotkeys, cached.supports_hotkeys),
    supports_dictation: bool(
      value.supports_dictation,
      cached.supports_dictation,
    ),
    supports_screen_capture: bool(
      value.supports_screen_capture,
      cached.supports_screen_capture,
    ),
    requires_input_consent: bool(
      value.requires_input_consent,
      cached.requires_input_consent,
    ),
  };
}

/** Ask Rust for the authoritative answer and cache it. Safe to call repeatedly. */
export function resolvePlatformCapabilities(): Promise<PlatformCapabilities> {
  if (!isTauri() || typeof window === "undefined") {
    return Promise.resolve(cached);
  }
  if (!inflight) {
    inflight = invoke<unknown>("denker_platform_capabilities")
      .then((raw) => {
        cached = parse(raw);
        resolved = true;
        return cached;
      })
      .catch(() => {
        // Let the next caller try again rather than pinning the seed for the
        // rest of the process.
        inflight = null;
        return cached;
      });
  }
  return inflight;
}

/** The current answer, readable synchronously from render. */
export function platformCapabilities(): PlatformCapabilities {
  return cached;
}

/**
 * Whether the answer is the authoritative one from Rust rather than the seed.
 *
 * Callers about to do something IRREVERSIBLE on a capability — skipping an
 * onboarding step, which gets persisted to the backend — must check this and
 * decline to act while it is false. Doing nothing is always the safe default
 * here: showing a step that turns out not to apply is corrected a moment later,
 * whereas skipping one that does apply is not corrected at all.
 */
export function platformCapabilitiesResolved(): boolean {
  return resolved;
}

// Resolve eagerly at module load so the authoritative answer is in place well
// before onboarding reaches its first permission step.
if (isTauri() && typeof window !== "undefined") {
  void resolvePlatformCapabilities();
}

/** The three OS grants the desktop app cares about. */
export type GatedPermission =
  | "microphone"
  | "accessibility"
  | "screen_recording";

/**
 * Whether it is worth asking the user to grant `permission` on this platform.
 *
 * One rule, both halves required: the OS has to actually gate it (otherwise
 * there is nothing to grant), and the feature behind it has to work here
 * (otherwise we are asking for something we cannot yet use).
 *
 * On macOS all three are gated and all three work, so this is true throughout
 * and nothing changes. On Windows, Accessibility and Screen Recording are not
 * gated at all — without this rule the post-onboarding repair flow would demand
 * grants the OS cannot issue, and the restart gate would block every launch
 * forever.
 */
export function permissionNeedsUserAction(
  permission: GatedPermission,
): boolean {
  const caps = platformCapabilities();
  switch (permission) {
    case "microphone":
      return (
        caps.microphone_permission_style !== "none" && caps.supports_dictation
      );
    case "accessibility":
      return caps.requires_accessibility_permission && caps.supports_hotkeys;
    case "screen_recording":
      return (
        caps.requires_screen_recording_permission &&
        caps.supports_screen_capture
      );
  }
}

/** Test-only — restore the module to its seeded state. */
export function __resetPlatformCapabilitiesForTests(): void {
  cached = isWindowsPlatform() ? WINDOWS_CAPABILITIES : MACOS_CAPABILITIES;
  resolved = false;
  inflight = null;
}

/** Test-only — stand in for a platform, so the real rules can be exercised
 *  rather than mocked around. Mocking this module would replace
 *  `permissionNeedsUserAction` along with the state it reads, leaving the rule
 *  itself untested. */
export function __setPlatformCapabilitiesForTests(
  capabilities: PlatformCapabilities,
  isResolved = true,
): void {
  cached = capabilities;
  // `isResolved: false` stands in for the window before Rust replies, so the
  // "never skip on the seed" guarantee can be tested rather than assumed.
  resolved = isResolved;
  inflight = isResolved ? Promise.resolve(capabilities) : null;
}
